import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useCart } from "../lib/cart-context";
import { useShop, DEFAULT_SHOP, SHOP_META } from "../lib/shop-context";

/**
 * /{shop}/checkout/success — polls the payment status endpoint after Stripe
 * redirects the user back with ?session_id={CHECKOUT_SESSION_ID}.
 * On "paid": clears the cart client-side and navigates to
 * `/{shop}/pedido/{order_id}` (Phase 32 hotfix 2 — pre-hotfix this navigated
 * to `/pedido/{id}` which 404'd because the OrderConfirmation route lives
 * inside the shop-scoped AppShell).
 */
export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh: refreshCart } = useCart();
  const shopCtx = useShop();
  // We are mounted inside AppShell so `shopCtx` is always present, but the
  // fallback keeps us honest if the route is ever hoisted.
  const shopPrefix = (shopCtx?.prefix) || SHOP_META[DEFAULT_SHOP].prefix;
  const attempts = useRef(0);
  const stopped = useRef(false);
  const [state, setState] = useState({ kind: "polling", message: "Confirmando tu pago…" });

  const MAX_ATTEMPTS = 15;
  const INTERVAL_MS = 2000;

  const poll = useCallback(async () => {
    if (stopped.current) return;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.status === "paid") {
        stopped.current = true;
        // Clear the cart (best-effort — the backend also nulls it on success).
        try {
          await api.delete("/cart");
        } catch (_) { /* noop */ }
        try {
          await refreshCart();
        } catch (_) { /* noop */ }
        toast.success("Pago confirmado. ¡Gracias por tu pedido!");
        if (data.related_id) {
          navigate(`${shopPrefix}/pedido/${data.related_id}`, { replace: true });
        }
        return;
      }
      if (data.status === "expired") {
        stopped.current = true;
        setState({
          kind: "expired",
          message:
            "La sesión de pago ha caducado. Puedes volver al checkout y probar de nuevo.",
        });
        return;
      }
      // pending → schedule another poll
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        stopped.current = true;
        setState({
          kind: "timeout",
          message:
            "Aún no hemos confirmado tu pago. Recibirás un email cuando se procese. Puedes cerrar esta ventana.",
        });
        return;
      }
      setTimeout(poll, INTERVAL_MS);
    } catch (_err) {
      attempts.current += 1;
      if (attempts.current >= MAX_ATTEMPTS) {
        stopped.current = true;
        setState({
          kind: "timeout",
          message:
            "No hemos podido comprobar el estado del pago. Recibirás un email cuando se procese.",
        });
        return;
      }
      setTimeout(poll, INTERVAL_MS);
    }
  }, [sessionId, refreshCart, navigate, shopPrefix]);

  useEffect(() => {
    if (!sessionId) {
      setState({ kind: "missing", message: "Falta el identificador de la sesión de pago." });
      return () => {};
    }
    stopped.current = false;
    attempts.current = 0;
    poll();
    return () => {
      stopped.current = true;
    };
  }, [sessionId, poll]);

  return (
    <div
      data-testid="checkout-success-page"
      className="mx-auto max-w-lg px-6 py-16 sm:py-24 text-center"
    >
      {state.kind === "polling" && (
        <>
          <div className="h-16 w-16 mx-auto rounded-full bg-[#F1FAF3] grid place-items-center">
            <Loader2 className="h-8 w-8 text-[#00953B] animate-spin" data-testid="checkout-success-spinner" />
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl font-black tracking-tight" data-testid="checkout-success-title">
            Confirmando tu pago…
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">
            {state.message} No cierres esta ventana.
          </p>
        </>
      )}
      {state.kind === "expired" && (
        <>
          <div className="h-16 w-16 mx-auto rounded-full bg-red-50 grid place-items-center">
            <XCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl font-black tracking-tight" data-testid="checkout-success-expired">
            Sesión de pago caducada
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">{state.message}</p>
          <Button
            asChild
            className="mt-6 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white h-11 px-6"
            data-testid="checkout-success-back-btn"
          >
            <Link to="/checkout">Volver al checkout</Link>
          </Button>
        </>
      )}
      {state.kind === "timeout" && (
        <>
          <div className="h-16 w-16 mx-auto rounded-full bg-amber-50 grid place-items-center">
            <CheckCircle2 className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="mt-6 text-2xl sm:text-3xl font-black tracking-tight" data-testid="checkout-success-timeout">
            Pago en proceso
          </h1>
          <p className="mt-3 text-neutral-500 text-sm">{state.message}</p>
          <Button
            asChild
            variant="outline"
            className="mt-6 rounded-full h-11 px-6"
            data-testid="checkout-success-home-btn"
          >
            <Link to="/">Volver al inicio</Link>
          </Button>
        </>
      )}
      {state.kind === "missing" && (
        <>
          <h1 className="text-2xl font-black tracking-tight">Enlace incompleto</h1>
          <p className="mt-3 text-neutral-500 text-sm">{state.message}</p>
          <Button
            asChild
            className="mt-6 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white h-11 px-6"
          >
            <Link to="/checkout">Volver al checkout</Link>
          </Button>
        </>
      )}
    </div>
  );
}
